/**
 * Mirrors the `filters:` block of `winsight-server`'s `reports/second-operation/report.yaml`,
 * which is authoritative: `from`/`to` and `factory` are required and the rest are optional.
 *
 * **The schema is built per factory and per view.** Per factory because department and sub-operation
 * codes are only meaningful inside one site, and the queries bind `= :factory` with no null
 * guard. Per view because Detail adds filters for its finer dimensions.
 *
 * Summary exposes only dimensions in its approved grain. Aggregated Detail adds filters for its
 * finer dimensions.
 */

import { format, subDays } from 'date-fns';

import { FACTORY_OPTIONS, type FactoryCode } from '@/lib/factories';
import { liveOptionsFor, type FilterOption, type FilterSchema } from '@/lib/filters';

/** Option values come from `report.yaml`'s `options:` block, never a hardcoded array (§5.4). */
const live = liveOptionsFor('second-operation');

const ISO = 'yyyy-MM-dd';

export type SecondOperationView = 'summary' | 'detail';

/**
 * Period the report opens on when the URL carries none. A week back to yesterday: the fact carries
 * roughly 140,000 scans a day across the five active factories, and today is still being recorded.
 *
 * Legacy pre-filled today's date only for holders of `sys:reportDefaultTime:edit` and left everyone
 * else with an empty, unbounded form. Every reader gets a period here, so that gate has nothing
 * left to withhold — report.yaml records it.
 */
export function defaultPeriod(today: Date = new Date()): { from: string; to: string } {
  return { from: format(subDays(today, 7), ISO), to: format(subDays(today, 1), ISO) };
}

/** Main body against the other garment parts, on the fact's `ismain`. */
const MAIN_BODY_OPTIONS: readonly FilterOption[] = [
  { value: 'Y', label: 'Main body' },
  { value: 'N', label: 'Other parts' },
];

export function secondOperationFilters(
  factory: FactoryCode,
  view: SecondOperationView,
): FilterSchema {
  const isDetail = view === 'detail';

  return [
    // Factory leads: it is required, every other list is scoped by it, and the codes below only
    // mean anything once it is chosen.
    {
      type: 'select',
      name: 'factory',
      label: 'Factory',
      required: true,
      options: FACTORY_OPTIONS,
      defaultValue: factory,
    },
    {
      type: 'date-range',
      name: 'period',
      label: 'Operation date',
      required: true,
      fromParam: 'from',
      toParam: 'to',
      defaultValue: defaultPeriod,
    },
    { type: 'text', name: 'b_no', label: 'B No' },
    { type: 'text', name: 'batch_no', label: 'Batch' },
    { type: 'text', name: 'set_group', label: 'Set group' },
    { type: 'text', name: 'partnos', label: 'Part No' },
    {
      type: 'select',
      name: 'sub_prcode',
      label: 'Sub operation',
      anyLabel: 'All sub operations',
      options: live('sub_prcode'),
    },

    ...(isDetail ? ([{ type: 'text', name: 'size', label: 'Size' }] as const) : []),

    {
      type: 'select',
      name: 'from_dept',
      label: 'From dept',
      anyLabel: 'All departments',
      options: live('from_dept'),
    },
    {
      type: 'select',
      name: 'to_dept',
      label: 'To dept',
      anyLabel: 'All departments',
      options: live('to_dept'),
    },

    ...(isDetail
      ? ([
          { type: 'text', name: 'location', label: 'Location' },
          { type: 'text', name: 'user_code', label: 'User ID' },
          {
            type: 'select',
            name: 'is_main_body',
            label: 'Part type',
            anyLabel: 'Main body and other parts',
            options: MAIN_BODY_OPTIONS,
          },
        ] as const)
      : []),
  ];
}
